import http from '@/api/http'
import { paymentsPageLimit } from '@/config/api'
import { majorToMinor } from '@/utils/majorToMinor'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface Payment {
  // One payment from GET `/payments` (cursor-paginated list).

  id: string
  // Payment id (UUID7).

  link: string | null
  // Optional external link when present.

  transaction_id: string | null
  // Bank/transaction id when present.

  text: string | null
  // Raw payment message text when the backend has source text.

  payment_status: string
  // API status (e.g. IDENTIFIED).

  person_id: string
  // Linked person id.

  person_name: string | null
  // Person display name when the API has resolved it.

  bill_id: number | null
  // Linked bill id when present.

  amount: number
  // Amount in major currency units (see backend contract); see `paymentAmountMinorUnits` for `MoneyCell`.

  date: string
  // ISO 8601 datetime of the payment.

  subscription_id: string | null
  // Subscription bought with this payment; null when none.

  subscription_type_id: string | null
  // Catalog plan of that subscription (see `SubscriptionType`); null when none.
}

/**
 * Balance-style conversion: major units to minor for `MoneyCell`, or `undefined` when the amount is missing or invalid.
 */
export function paymentAmountMinorUnits(payment: Pick<Payment, 'amount'>): number | undefined {
  return majorToMinor(payment.amount)
}

interface PaymentsResponse extends PageMeta {
  // Paginated list payload from GET `/payments`.

  items: Payment[]
  // Page of payments.
}

export interface CreatePaymentPayload {
  // Request body for POST `/payments`.

  link: string | null
  // Optional external URL; null when absent.

  transaction_id: string | null
  // Bank/transaction id; null when absent.

  text: string | null
  // Raw payment message text; null when absent.

  payment_status: string
  // Send 'NONE' for a newly recorded payment.

  person_id: string | null
  // Person to link; null when unresolved.

  person_name: string | null
  // Person display name; null when unresolved.

  bill_id: number | null
  // Bill to link; null when unlinked.

  amount: number
  // Non-negative float in major currency units.

  date: string
  // ISO 8601 datetime string (timezone-aware, e.g. UTC Z suffix).

  subscription_type_id?: string
  // Optional subscription to purchase with this payment (create only); requires `person_id`.
  // The subscription starts at the payment `date` unless `valid_from` is given.
}

/**
 * Creates a new payment via POST `/payments`.
 */
export async function createPayment(payload: CreatePaymentPayload): Promise<Payment> {
  const { data } = await http.post<Payment>('/payments', payload)
  if (import.meta.env.DEV) {
    console.log('[createPayment] POST /payments response', data)
  }
  return data
}

/**
 * Fetches a page of payments. Same cursor/limit contract as `fetchClients`.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchPayments(cursor?: number): Promise<PaymentsResponse> {
  const { data } = await http.get<PaymentsResponse>('/payments', {
    params: cursorParams(paymentsPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchPayments] GET /payments response', data)
  }
  return data
}

/**
 * Fetches one payment by id for the payment info card.
 */
export async function fetchPayment(paymentId: string): Promise<Payment> {
  const { data } = await http.get<Payment>(`/payments/${paymentId}`)
  if (import.meta.env.DEV) {
    console.log(`[fetchPayment] GET /payments/${paymentId} response`, data)
  }
  return data
}

export interface UpdatePaymentPayload {
  // Request body for PATCH `/payments/{id}`.

  link: string | null
  // Optional external URL; null to clear.

  transaction_id: string | null
  // Bank/transaction id; null to clear.

  text: string | null
  // Raw payment message text; null to clear.

  payment_status: string
  // Updated status string.

  person_id: string | null
  // Person to link; null to unlink.

  person_name: string | null
  // Person display name; null to clear.

  bill_id: number | null
  // Bill to link; null to unlink.

  amount: number
  // Updated amount in major currency units.

  date: string
  // Updated ISO 8601 datetime string.

  subscription_type_id?: string | null
  // Subscription linked to the payment: a plan id adds or switches it, an explicit
  // null removes it, and omitting the field keeps it unchanged.
}

export async function updatePayment(id: string, payload: UpdatePaymentPayload): Promise<Payment> {
  const { data } = await http.patch<Payment>(`/payments/${id}`, payload)
  if (import.meta.env.DEV) console.log(`[updatePayment] PATCH /payments/${id} response`, data)
  return data
}

/**
 * Deletes a payment by id.
 * @param id - Payment id to delete.
 */
export async function deletePayment(id: string): Promise<void> {
  await http.delete(`/payments/${id}`)
  if (import.meta.env.DEV) console.log(`[deletePayment] DELETE /payments/${id}`)
}
